import { NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import {
  BadgePercent,
  Boxes,
  Image,
  Headphones,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Users,
  GitBranch,
  Landmark,
  Trophy,
  Award,
  ScrollText,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import { getProfileImage } from "../core/common/api";
import AppImage from "./AppImage";
import { hasPermission, AdminPermissionKey } from "../core/common/adminPermissions";

type SidebarProps = {
  isOpen: boolean;
  onClose: () => void;
};

const navItems: Array<{
  route: string;
  label: string;
  permission: AdminPermissionKey;
  icon: typeof LayoutDashboard;
  end?: boolean;
}> = [
  { route: "/dashboard", label: "Dashboard", permission: "dashboard", icon: LayoutDashboard },
  { route: "/admins", label: "All Admins", permission: "admins", icon: Shield },
  { route: "/users", label: "Register Users", permission: "users", icon: Users, end: true },
  { route: "/sale-users", label: "Sale Users", permission: "users", icon: ShoppingBag, end: true },
  { route: "/users/reward-achievers", label: "Reward Achievers", permission: "users", icon: Award },
  { route: "/users/tree", label: "Network Tree", permission: "users", icon: GitBranch },
  { route: "/withdrawals", label: "Withdrawals", permission: "users", icon: Landmark },
  { route: "/income-history", label: "Income History", permission: "users", icon: ScrollText },
  { route: "/profit-share", label: "Profit Share", permission: "users", icon: Trophy },
  { route: "/club-income", label: "Club Income", permission: "settings", icon: SlidersHorizontal },
  { route: "/products", label: "All Products", permission: "products", icon: ShoppingBag },
  { route: "/categories", label: "All Categories", permission: "categories", icon: Boxes },
  { route: "/orders", label: "All Orders", permission: "orders", icon: ShoppingCart },
  { route: "/coupons", label: "All Coupons", permission: "coupons", icon: BadgePercent },
  { route: "/banners", label: "All Banners", permission: "banners", icon: Image },
  { route: "/reviews", label: "All Reviews", permission: "reviews", icon: MessageSquare },
  { route: "/order-feedback", label: "Order Feedback", permission: "reviews", icon: Star },
  { route: "/support", label: "User Support", permission: "support", icon: Headphones },
  { route: "/settings", label: "Settings", permission: "settings", icon: Settings },
];

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { logout } = useAuth();
  const [profileData, setProfileData] = useState<any>(null);
  const profileImage = getProfileImage(profileData);

  useEffect(() => {
    const loadProfile = () => {
      const storedProfile = localStorage.getItem("adminProfile");
      try {
        setProfileData(storedProfile ? JSON.parse(storedProfile) : null);
      } catch {
        setProfileData(null);
      }
    };

    loadProfile();
    window.addEventListener("adminProfile:updated", loadProfile);
    return () => window.removeEventListener("adminProfile:updated", loadProfile);
  }, []);

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      if (profileData?.role === "superadmin") return true;
      return hasPermission(item.permission);
    });
  }, [profileData]);

  const handleLogout = async () => {
    const confirmation = await Swal.fire({
      title: "Log out?",
      text: "You will need to sign in again to access the admin panel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Log out",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#1e3a6e",
    });

    if (!confirmation.isConfirmed) return;

    onClose();
    await logout();
  };

  const closeOnMobile = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close sidebar"
          onClick={onClose}
          className="sidebar-backdrop"
        />
      )}

      <aside className={`app-sidebar ${isOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <img className="brand-logo" src={logo} alt="Riseway" />
          <span className="brand-monogram" aria-label="Riseway">
            RW
          </span>

          <button
            type="button"
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.route}
                to={item.route}
                end={item.end}
                onClick={closeOnMobile}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
              >
                <span className="nav-icon">
                  <Icon size={18} strokeWidth={2} />
                </span>
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-user">
          <AppImage
            src={profileImage}
            alt={profileData?.name || "Admin"}
            crossOrigin="anonymous"
          />

          <div>
            <strong>{profileData?.name || "Admin"}</strong>
            <small>{profileData?.role || "Admin"}</small>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            aria-label="Logout"
            title="Logout"
          >
            <LogOut size={18} strokeWidth={2} />
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
